/**
 * 模块首页（登录后第一屏）
 *
 * 三个业务模块：
 *  - 招标文件编写（规划中）
 *  - 招标信息筛选（规划中）
 *  - 投标文件编写（已实现）
 *
 * 点击模块卡片进入对应模块工作区；已实现模块进入原有链路，规划中模块进入骨架页。
 */
import React from 'react';
import {
  ArrowRightIcon,
  DocumentTextIcon,
  FunnelIcon,
  PencilSquareIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { UserInfo } from '../types';
import { APP_MODULES, AppModuleId, AppModuleMeta } from './modules';

interface ModuleHomeProps {
  user: UserInfo;
  isAdmin: boolean;
  lastUsedModuleId: AppModuleId | null;
  allowedFeatures: string[] | null;
  onSelect: (id: AppModuleId) => void;
  onOpenAdmin: () => void;
  onOpenSettings: () => void;
  onLogout: () => void;
}

const MODULE_ICONS: Record<AppModuleId, React.ComponentType<React.ComponentProps<'svg'>>> = {
  'tender-doc': DocumentTextIcon,
  'bid-screening': FunnelIcon,
  'bid-writing': PencilSquareIcon,
};

/** 模块 → 需要的授权功能（与后端门禁 feature 一致；未授权则前端禁用进入） */
const MODULE_FEATURE: Record<AppModuleId, string> = {
  'tender-doc': 'tender-doc',
  'bid-screening': 'bid-screening',
  'bid-writing': 'bid-writing',
};

function roleLabel(isAdmin: boolean, role: string): string {
  if (isAdmin) return '管理员';
  if (role === 'enterprise') return '企业用户';
  if (role === 'agent') return '代理用户';
  return '普通用户';
}

const ModuleHome: React.FC<ModuleHomeProps> = ({
  user,
  isAdmin,
  lastUsedModuleId,
  allowedFeatures,
  onSelect,
  onOpenAdmin,
  onOpenSettings,
  onLogout,
}) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部栏 */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-base font-semibold text-gray-900">标书智能体</span>
            <span className="text-xs text-gray-400">工作台</span>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-xs text-gray-500">
              {user.display_name || user.username}
              <span
                className={`ml-1 inline-flex items-center px-1 py-0.5 rounded text-xs ${
                  isAdmin ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {roleLabel(isAdmin, user.role)}
              </span>
            </span>
            {isAdmin && (
              <button
                onClick={onOpenAdmin}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                title="管理面板"
              >
                <Cog6ToothIcon className="w-4 h-4 mr-1.5" />
                管理面板
              </button>
            )}
            <button
              onClick={onOpenSettings}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 hover:text-gray-800 transition-colors"
              title="我的设置"
            >
              <UserCircleIcon className="w-4 h-4 mr-1.5" />
              我的设置
            </button>
            <button
              onClick={onLogout}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 hover:text-gray-800 transition-colors"
              title="退出登录"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4 mr-1.5" />
              退出登录
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">选择模块</h1>
          <p className="mt-2 text-sm text-gray-500">
            系统分为招标文件编写、招标信息筛选、投标文件编写三个模块，请选择要进入的模块。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {APP_MODULES.map((module) => (
            <ModuleCard
              key={module.id}
              module={module}
              isLastUsed={module.id === lastUsedModuleId}
              allowedFeatures={allowedFeatures}
              onSelect={onSelect}
            />
          ))}
        </div>

        <div className="mt-8 text-center text-xs text-gray-400">
          欢迎访问 AI 智能体--workeasy，版权：北京法华科技有限公司
        </div>
      </div>
    </div>
  );
};

interface ModuleCardProps {
  module: AppModuleMeta;
  isLastUsed: boolean;
  allowedFeatures: string[] | null;
  onSelect: (id: AppModuleId) => void;
}

const ModuleCard: React.FC<ModuleCardProps> = ({ module, isLastUsed, allowedFeatures, onSelect }) => {
  const Icon = MODULE_ICONS[module.id];
  const isReady = module.status === 'ready';
  const feature = MODULE_FEATURE[module.id];
  const allowed =
    allowedFeatures === null || allowedFeatures.length === 0 || allowedFeatures.includes(feature);

  return (
    <button
      type="button"
      onClick={() => allowed && onSelect(module.id)}
      disabled={!allowed}
      className={`group text-left bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-primary-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all flex flex-col h-full p-5 ${
        allowed ? '' : 'opacity-50 cursor-not-allowed'
      }`}
    >
      <div className="flex items-start justify-between">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isReady ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-500'
          }`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex items-center space-x-1.5">
          {isLastUsed && (
            <span className="px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-600 border border-blue-200">
              上次使用
            </span>
          )}
          <span
            className={`px-1.5 py-0.5 rounded text-xs border ${
              isReady
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}
          >
            {module.statusLabel}
          </span>
          {!allowed && (
            <span className="px-1.5 py-0.5 rounded text-xs border bg-red-50 text-red-700 border-red-200">
              未授权
            </span>
          )}
        </div>
      </div>

      <h2 className="mt-4 text-base font-semibold text-gray-900">{module.name}</h2>
      <p className="mt-2 text-sm text-gray-500 leading-relaxed">{module.summary}</p>

      <div className="mt-4 text-xs text-gray-400">适用：{module.audience}</div>

      <div className="mt-4 flex-1">
        <div className="text-xs font-medium text-gray-500 mb-2">
          {isReady ? '已具备能力' : '规划能力'}
        </div>
        <ul className="space-y-1.5">
          {module.capabilities.slice(0, 4).map((item) => (
            <li key={item} className="flex items-start text-xs text-gray-500">
              <span className="mt-1.5 mr-2 w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
              <span className="leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between text-sm font-medium text-primary-600">
        <span>{allowed ? '进入模块' : '未授权，请联系管理员'}</span>
        {allowed && <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />}
      </div>
    </button>
  );
};

export default ModuleHome;
